// This is example of menu item without group for horizontal layout. There will be no children.
// assets
import {
  AlignLeftOutlined,
  AppstoreOutlined,
  BankOutlined,
  BarChartOutlined,
  DollarCircleOutlined,
  DollarOutlined,
  EyeFilled,
  GroupOutlined,
  LoginOutlined,
  MoneyCollectOutlined,
  PhoneOutlined,
  QuestionOutlined,
  SettingOutlined,
  SlidersOutlined,
  SolutionOutlined,
  TableOutlined,
  TrophyOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useTheme } from '@mui/material/styles';
import { Briefcase01 } from '@trm/ui-kit-admin/v7/icons';
import type { CSSProperties } from 'react';
// third-party
import { FormattedMessage } from 'react-intl';
import AccountsIcon from '~/assets/images/accounts.svg';
import ActivityIcon from '~/assets/images/activitylog.svg';
import AdministrationIcon from '~/assets/images/administration.svg';
import HelpIcon from '~/assets/images/help.svg';
import PayoutsIcon from '~/assets/images/payouts.svg';
import PromosIcon from '~/assets/images/promos.svg';
import UsersIcon from '~/assets/images/users.svg';
// type
import { NavItemType } from '~/types/menu';
import { PagePolicies } from '~/utils/roles';

const AccountsIconComponent = () => {
  const theme = useTheme();
  return (
    <img
      src={AccountsIcon}
      alt="accounts"
      style={{
        filter: theme.palette.mode === 'dark' ? 'invert(1)' : 'none',
      }}
    />
  );
};

const PayoutsIconComponent = () => {
  const theme = useTheme();
  return (
    <img
      src={PayoutsIcon}
      alt="payouts"
      style={{
        filter: theme.palette.mode === 'dark' ? 'invert(1)' : 'none',
      }}
    />
  );
};

const HelpIconComponent = () => {
  const theme = useTheme();
  return (
    <img
      src={HelpIcon}
      alt="help"
      style={{
        filter: theme.palette.mode === 'dark' ? 'invert(1)' : 'none',
      }}
    />
  );
};

const UsersIconComponent = () => {
  const theme = useTheme();
  return (
    <img
      src={UsersIcon}
      alt="users"
      style={{ filter: theme.palette.mode === 'dark' ? 'invert(1)' : 'none' }}
    />
  );
};

const PromosIconComponent = () => {
  const theme = useTheme();
  return (
    <img
      src={PromosIcon}
      alt="promos"
      style={{ filter: theme.palette.mode === 'dark' ? 'invert(1)' : 'none' }}
    />
  );
};

const ActivityIconComponent = () => {
  const theme = useTheme();
  return (
    <img
      src={ActivityIcon}
      alt="activity"
      style={{ filter: theme.palette.mode === 'dark' ? 'invert(1)' : 'none' }}
    />
  );
};

const AdministrationIconComponent = () => {
  const theme = useTheme();
  return (
    <img
      src={AdministrationIcon}
      alt="administration"
      style={{ filter: theme.palette.mode === 'dark' ? 'invert(1)' : 'none' }}
    />
  );
};

// icons
const CaseManagementIconComponent = ({ style }: { style?: CSSProperties }) => (
  <Briefcase01 style={{ ...style, width: '1em', height: '1em' }} />
);

const icons = {
  BarChartOutlined,
  DollarOutlined,
  LoginOutlined,
  DollarCircleOutlined,
  TrophyOutlined,
  PhoneOutlined,
  QuestionOutlined,
  TableOutlined,
  BankOutlined,
  MoneyCollectOutlined,
  GroupOutlined,
  SettingOutlined,
  UserOutlined,
  SlidersOutlined,
  AppstoreOutlined,
  SolutionOutlined,
  AlignLeftOutlined,
  EyeFilled,
  AccountsIcon: AccountsIconComponent,
  PayoutsIcon: PayoutsIconComponent,
  HelpIcon: HelpIconComponent,
  UsersIcon: UsersIconComponent,
  PromosIcon: PromosIconComponent,
  ActivityIcon: ActivityIconComponent,
  AdministrationIcon: AdministrationIconComponent,
  CaseManagementIcon: CaseManagementIconComponent,
};

// ==============================|| MENU ITEMS - SAMPLE PAGE ||============================== //

const menu: NavItemType = {
  id: 'dashboard',
  type: 'group',
  children: [
    {
      id: 'adminusers',
      title: (
        <strong>
          <FormattedMessage id="Users" />
        </strong>
      ),
      type: 'item',
      url: '/dashboard/admin/users',
      policies: PagePolicies.AdminUsersPage,
      beta: { kind: 'opt-in', page: 'users' },
      icon: icons.UsersIcon,
    },
    {
      id: 'adminaccounts',
      title: (
        <strong>
          <FormattedMessage id="Accounts" />
        </strong>
      ),
      type: 'item',
      url: '/dashboard/admin/accounts',
      policies: PagePolicies.AdminAccountsPage,
      beta: { kind: 'opt-in', page: 'accounts' },
      icon: icons.AccountsIcon,
    },
    {
      id: 'adminBulkActions',
      title: (
        <strong>
          <FormattedMessage id="Bulk Actions" />
        </strong>
      ),
      type: 'item',
      url: '/dashboard/admin/bulk-actions',
      policies: PagePolicies.AdminBulkActionsPage,
      icon: icons.AlignLeftOutlined,
      beta: { kind: 'preview' },
    },
    {
      id: 'adminTraderProfilesModeration',
      title: (
        <strong>
          <FormattedMessage id="Trader Handle Moderation" />
        </strong>
      ),
      type: 'item',
      url: '/dashboard/admin/trader-profiles/moderation',
      policies: PagePolicies.AdminTraderProfilesModerationPage,
      icon: icons.SolutionOutlined,
      beta: { kind: 'preview' },
    },
    {
      id: 'admincoupons',
      title: (
        <strong>
          <FormattedMessage id="Promos" />
        </strong>
      ),
      type: 'item',
      url: '/dashboard/admin/coupons',
      policies: PagePolicies.AdminCouponsPage,
      icon: icons.PromosIcon,
    },
    {
      id: 'adminpayouts',
      title: (
        <strong>
          <FormattedMessage id="Payouts" />
        </strong>
      ),
      type: 'collapse',
      policies: PagePolicies.AdminPayoutsPage,
      icon: icons.PayoutsIcon,
      children: [
        {
          id: 'admintracker',
          title: (
            <strong>
              <FormattedMessage id="Tracker" />
            </strong>
          ),
          type: 'item',
          url: '/dashboard/admin/payouts',
          policies: PagePolicies.AdminPayoutBatchesPage,
        },
        {
          id: 'adminpayoutbatch',
          title: (
            <strong>
              <FormattedMessage id="Payout Batches" />
            </strong>
          ),
          type: 'item',
          url: '/dashboard/admin/payouts/batches',
          policies: PagePolicies.AdminPayoutBatchesPage,
        },
      ],
    },
    {
      id: 'adminaffiliates',
      title: (
        <strong>
          <FormattedMessage id="Affiliates" />
        </strong>
      ),
      type: 'item',
      url: '/dashboard/admin/affiliates',
      policies: PagePolicies.AdminAffiliatesPage,
      icon: icons.DollarCircleOutlined,
    },
    {
      id: 'adminkyc',
      title: (
        <strong>
          <FormattedMessage id="Identity Verification" />
        </strong>
      ),
      type: 'item',
      url: '/dashboard/admin/idv',
      policies: PagePolicies.AdminKYCPage,
      icon: icons.EyeFilled,
    },
    {
      id: 'adminPurchaseOrders',
      title: (
        <strong>
          <FormattedMessage id="Purchases" defaultMessage="Purchases" />
        </strong>
      ),
      type: 'item',
      url: '/dashboard/admin/purchase-orders',
      policies: PagePolicies.AdminTransactionsPage,
      icon: icons.DollarOutlined,
    },
    {
      id: 'adminTransactions',
      title: (
        <strong>
          <FormattedMessage id="Transactions" />
        </strong>
      ),
      type: 'collapse',
      policies: PagePolicies.AdminTransactionsPage,
      icon: icons.DollarOutlined,
      children: [
        {
          id: 'nmiTransactions',
          title: (
            <strong>
              <FormattedMessage id="NMI" />
            </strong>
          ),
          type: 'item',
          url: '/dashboard/admin/purchases',
          policies: PagePolicies.AdminTransactionsPage,
        },
        {
          id: 'nuveiTransactions',
          title: (
            <strong>
              <FormattedMessage id="Others" />
            </strong>
          ),
          type: 'item',
          url: '/dashboard/admin/transactions',
          policies: PagePolicies.AdminTransactionsPage,
        },
      ],
    },
    ...(localStorage.showProductFeesPage
      ? [
          {
            id: 'admintradingproductfees',
            title: (
              <strong>
                <FormattedMessage id="ProductFees" />
              </strong>
            ),
            type: 'item',
            url: '/dashboard/admin/trading-product-fees',
            policies: PagePolicies.AdminProductsPage,
            icon: icons.DollarCircleOutlined,
          },
        ]
      : []),
    {
      id: 'adminTraderProfiles',
      title: (
        <strong>
          <FormattedMessage id="Trader Profiles" />
        </strong>
      ),
      type: 'collapse',
      policies: PagePolicies.AdminTraderProfilesPage,
      icon: icons.SolutionOutlined,
      children: [
        {
          id: 'adminTraderProfilesSearch',
          title: (
            <strong>
              <FormattedMessage id="Search Handle" />
            </strong>
          ),
          type: 'item',
          url: '/dashboard/admin/trader-profiles/search',
          policies: PagePolicies.AdminTraderProfilesPage,
        },
        {
          id: 'adminTraderProfilesValidate',
          title: (
            <strong>
              <FormattedMessage id="Validate Handle" />
            </strong>
          ),
          type: 'item',
          url: '/dashboard/admin/trader-profiles/validate',
          policies: PagePolicies.AdminValidateHandlePage,
        },
      ],
    },
    {
      id: 'adminCases',
      title: (
        <strong>
          <FormattedMessage id="Case Management" />
        </strong>
      ),
      type: 'collapse',
      icon: icons.CaseManagementIcon,
      children: [
        {
          id: 'casesInvestigations',
          title: (
            <strong>
              <FormattedMessage id="Investigations" />
            </strong>
          ),
          type: 'item',
          url: '/dashboard/admin/cases/investigations',
          policies: PagePolicies.AdminCaseInvestigationsPage,
        },
        {
          id: 'casesBans',
          title: (
            <strong>
              <FormattedMessage id="Bans" />
            </strong>
          ),
          type: 'item',
          url: '/dashboard/admin/cases/bans',
          policies: PagePolicies.AdminCaseBansPage,
        },
        // Case Settings is temporarily hidden from the nav for the release. The page and
        // its route still exist and remain reachable at /dashboard/admin/cases/settings.
      ],
    },
    {
      id: 'adminlog',
      title: (
        <strong>
          <FormattedMessage id="Activity Log" />
        </strong>
      ),
      type: 'item',
      url: '/dashboard/admin/log',
      policies: PagePolicies.AdminLogPage,
      icon: icons.ActivityIcon,
    },
    {
      id: 'adminorganization',
      title: (
        <strong>
          <FormattedMessage id="Administration" />
        </strong>
      ),
      type: 'item',
      url: '/dashboard/admin/organization',
      policies: PagePolicies.AdminOrganizationPage,
      icon: icons.AdministrationIcon,
    },
    {
      id: 'adminimpactgroups',
      title: (
        <strong>
          <FormattedMessage id="Impact Groups" />
        </strong>
      ),
      type: 'item',
      url: '/dashboard/admin/impact-groups',
      policies: PagePolicies.AdminImpactGroupsPage,
      icon: icons.GroupOutlined,
    },
    {
      id: 'helpcenter',
      title: (
        <strong>
          <FormattedMessage id="Help Center" />
        </strong>
      ),
      type: 'item',
      url: 'https://help.topstep.com/en',
      external: true,
      target: true,
      icon: icons.HelpIcon,
    },
  ],
};

export default menu;
